/**
 * Mutation hook for deleting a location tag
 *
 * @see GitLab #999 — Support for adding location tags
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../../services/api';
import {
  getOfflineLocationTagList,
  mirrorLocationTagsToOfflineCache,
  runSerializedLocationTagOfflineMutation,
} from '../../services/locationTagOfflineSync';
import { networkService } from '../../services/network';
import { offlineCache } from '../../services/offlineCache';
import { locationTagsKeys } from '../queries/useLocationTags';

interface DeleteLocationTagParams {
  requestId: string;
  tagId: number;
}

export const useDeleteLocationTag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    networkMode: 'always',
    mutationFn: async ({ requestId, tagId }: DeleteLocationTagParams) => {
      if (!networkService.isConnected()) {
        return runSerializedLocationTagOfflineMutation(async () => {
          const existingTags = await getOfflineLocationTagList(requestId);
          if (!existingTags.some(tag => tag.id === tagId)) {
            throw new Error('Location tag not found');
          }
          if (await offlineCache.isLocationTagAssigned(requestId, tagId)) {
            throw new Error(
              'Cannot delete location tag because one or more equipment units are assigned to it. Unassign all units first.',
            );
          }
          const removedPendingCreate =
            tagId < 0 &&
            (await offlineCache.removePendingLocationTagCreate(
              requestId,
              tagId,
            ));
          if (!removedPendingCreate) {
            await offlineCache.addLocationTagToOfflineQueue(
              requestId,
              'delete',
              undefined,
              tagId,
            );
          }
          const filteredTags = existingTags.filter(tag => tag.id !== tagId);
          await mirrorLocationTagsToOfflineCache(requestId, filteredTags);
          return { requestId, tagId };
        });
      }

      await api.delete(`/requests/${requestId}/location-tags/${tagId}`);
      const cachedTags = await offlineCache.getCachedLocationTags(requestId);
      await mirrorLocationTagsToOfflineCache(
        requestId,
        cachedTags.filter(tag => tag.id !== tagId),
      );
      return { requestId, tagId };
    },
    onSuccess: ({ requestId }) => {
      queryClient.invalidateQueries({
        queryKey: locationTagsKeys.list(requestId),
      });
    },
    meta: {
      errorMessage: 'Failed to delete location tag. Please try again.',
    },
  });
};
